import { useState } from 'react';
import { useCerebroAPI } from '@/hooks/useCerebroAPI';
import { Play, Square, Circle, SkipForward, Repeat, Bell, Gauge, Volume2, Settings } from 'lucide-react';

export function DAWControls() {
  const { sendDAW } = useCerebroAPI();
  const [bpm, setBpm] = useState(120);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [currentBar, setCurrentBar] = useState(1);

  const handlePlay = async () => {
    const result = await sendDAW(isPlaying ? 'transport.pause' : 'transport.play');
    if (result) setIsPlaying(!isPlaying);
  };

  const handleStop = async () => {
    const result = await sendDAW('transport.stop');
    if (result) {
      setIsPlaying(false);
      setIsRecording(false);
    }
  };

  const handleRecord = async () => {
    const result = await sendDAW('transport.record');
    if (result) setIsRecording(!isRecording);
  };

  const handleLoop = async () => {
    const result = await sendDAW('transport.loop', 'global', { enabled: !loopEnabled });
    if (result) setLoopEnabled(!loopEnabled);
  };

  const handleMetronome = async () => {
    const result = await sendDAW('transport.metronome', 'global', { enabled: !metronomeEnabled });
    if (result) setMetronomeEnabled(!metronomeEnabled);
  };

  return (
    <div className="bg-void/95 border border-border-subtle rounded-xl overflow-hidden">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border-subtle bg-surface/30">
        <Settings size={14} className="text-spice" />
        <span className="text-label text-spice">DAW CONTROLS</span>
      </div>

      <div className="p-3 space-y-3">
        {/* Transport */}
        <div className="flex items-center justify-center gap-2">
          <TransportButton
            icon={SkipForward}
            onClick={() => { setCurrentBar(1); sendDAW('transport.rewind'); }}
            label="Rewind"
          />
          <TransportButton
            icon={Circle}
            onClick={handleRecord}
            active={isRecording}
            activeClass="text-fire bg-fire/10"
            label="Record"
          />
          <TransportButton
            icon={isPlaying ? Square : Play}
            onClick={handlePlay}
            active={isPlaying}
            activeClass="text-neon bg-neon/10 shadow-glow-neon"
            label={isPlaying ? 'Pause' : 'Play'}
            size={20}
          />
          <TransportButton
            icon={Square}
            onClick={handleStop}
            label="Stop"
          />
          <TransportButton
            icon={SkipForward}
            onClick={() => sendDAW('transport.forward')}
            label="Forward"
          />
        </div>

        {/* Secondary Controls */}
        <div className="flex items-center justify-center gap-2">
          <TransportButton
            icon={Repeat}
            onClick={handleLoop}
            active={loopEnabled}
            activeClass="text-spice"
            label="Loop"
            size={14}
          />
          <TransportButton
            icon={Bell}
            onClick={handleMetronome}
            active={metronomeEnabled}
            activeClass="text-gold"
            label="Metronome"
            size={14}
          />
          <TransportButton
            icon={Gauge}
            onClick={() => sendDAW('transport.countin')}
            label="Count-in"
            size={14}
          />
        </div>

        {/* BPM & Time */}
        <div className="flex items-center gap-3 pt-2 border-t border-border-subtle">
          <div className="flex items-center gap-1.5">
            <span className="text-label text-tertiary">BPM</span>
            <input
              type="number"
              value={bpm}
              onChange={(e) => {
                const val = Number(e.target.value);
                setBpm(val);
                sendDAW('transport.tempo', 'global', { tempo: val });
              }}
              className="w-14 bg-surface border border-border-subtle rounded-sm px-1 py-0.5 text-mono-sm text-primary text-center focus:border-spice outline-none"
              min="20"
              max="999"
            />
          </div>

          <div className="flex items-center gap-0.5">
            <input type="number" defaultValue={4} className="w-6 bg-surface border border-border-subtle rounded-sm px-0.5 py-0.5 text-mono-sm text-primary text-center" min="1" max="32" />
            <span className="text-tertiary text-body-sm">/</span>
            <input type="number" defaultValue={4} className="w-6 bg-surface border border-border-subtle rounded-sm px-0.5 py-0.5 text-mono-sm text-primary text-center" min="1" max="32" />
          </div>

          <div className="ml-auto flex items-center gap-0.5 bg-surface/50 rounded-sm px-2 py-0.5">
            <span className="text-mono-lg text-primary tabular-nums">{String(currentBar).padStart(3, '0')}</span>
            <span className="text-tertiary">|</span>
            <span className="text-mono-lg text-primary tabular-nums">01</span>
            <span className="text-tertiary">|</span>
            <span className="text-mono-sm text-tertiary tabular-nums">000</span>
          </div>
        </div>

        {/* Volume */}
        <div className="flex items-center gap-2 pt-1">
          <Volume2 size={14} className="text-tertiary" />
          <input
            type="range"
            min="0"
            max="100"
            defaultValue="75"
            className="flex-1"
            onChange={(e) => sendDAW('master.volume', 'bus_master', { volume_db: Number(e.target.value) - 100 })}
          />
          <span className="text-mono-sm text-tertiary w-8 text-right">-6dB</span>
        </div>

        {/* Track Quick Actions */}
        <div className="grid grid-cols-4 gap-1 pt-1">
          {['Track 1', 'Track 2', 'Track 3', 'Master'].map((t, i) => (
            <button
              key={t}
              onClick={() => sendDAW(i === 3 ? 'track.solo' : 'track.mute', i === 3 ? 'bus_master' : `track_${i + 1}`)}
              className="px-2 py-1 rounded-md bg-surface/30 text-mono-sm text-secondary hover:bg-hover hover:text-primary transition-all"
            >
              {t}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function TransportButton({
  icon: Icon,
  onClick,
  active,
  activeClass,
  label,
  size = 16
}: {
  icon: typeof Play;
  onClick: () => void;
  active?: boolean;
  activeClass?: string;
  label: string;
  size?: number;
}) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`p-2 rounded-lg transition-all ${
        active
          ? activeClass || 'text-primary bg-hover'
          : 'text-secondary hover:text-primary hover:bg-hover'
      }`}
    >
      <Icon size={size} />
    </button>
  );
}
